import { useLazyQuery, useQuery } from '@apollo/client/react';
import React, { useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import {
    GET_ALL_ACCESSORIALS,
    GET_ALL_CREWS,
    GET_ALL_CUSTOMERS,
    GET_ALL_EPCOMPANIES,
    GET_ALL_REGIONS,
    GET_ALL_STATES,
} from '../../graphql/client/queries';
import { PermissionEnum } from '../../types/PermissionEnum';
import {
    GetAllAccessorials,
    GetAllAccessorials_getAllAccessorials as AccessorialType,
    GetAllCrews,
    GetAllCrewsVariables,
    GetAllCrews_getAllCrews as CrewType,
    GetAllCustomers,
    GetAllCustomers_getAllCustomers as CustomerType,
    GetAllEpCompanies,
    GetAllEpCompanies_getAllEpCompanies as EPCompanyType,
    GetAllRegions,
    GetAllRegions_getAllRegions as RegionType,
    GetAllRegions_getAllRegions_districts as DistrictType,
    GetAllStates,
    GetAllStates_getAllStates as StateType,
} from '../../types/schemaTypes';
import { useAuthContext } from '../Auth/AuthProvider';
import { CustomButton, DateTimePicker } from '../_common';
import SearchableDropdown from '../_common/SearchableDropdown/SearchableDropdown';
import { Textfield } from '../_common/Textfield/Textfield';
import './AddJob.scss';
import useAddJobProfileForm from './useAddJobProfileForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { geoFenceTypes } from '../_common/Geofence/GeofenceTypes';
import GeofenceMap from '../_common/Geofence/GeofenceMap';
import Switch from "react-switch";

library.add(fas);

interface Option {
    id: number | string;
    name: string | number;
}

interface Suggestion {
    id: string | number;
    name: string;
}

interface LatLng {
    lat: number;
    lng: number;
}

const customerCrewText = 'This customer does not have any crew.';

const defaultGeoFenceRadius = 300;

const InitialModal = (): React.ReactElement => {
    const [show, setShow] = useState(false);
    const [showStagingMap, setShowStagingMap] = useState(false);
    const [showWellSiteMap, setShowWellSiteMap] = useState(false);
    const initialCrews: Suggestion[] = [];
    const [crews, setCrews] = useState(initialCrews);
    const [customerCrewError, setCustomerCrewError] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const initialDistricts: Option[] = [];
    const [districts, setDistricts] = useState(initialDistricts);
    const [districtName, setDistrictName] = useState('');
    const [isBellyDump, setIsBellyDump] = useState(false);
    const [getCrewsByCustomer, { data: crewsData, loading: crewsLoading }] = useLazyQuery<GetAllCrews, GetAllCrewsVariables>(GET_ALL_CREWS);
    const {
        inputs,
        fieldErrors,
        handleInputChange,
        handleAutoCompleteChange,
        handleDateInputChange,
        handleDropdownChange,
        handleSubmit,
        validateLatitude,
        validateLongitude,
        validateStagingLatitude,
        validateStagingLongitude,
        validateZipNumber,
        validatePhoneNumber,
        resetFormState,
        mutationError,
        setMutationError,
    } = useAddJobProfileForm();
    const { data: accessorialsData } = useQuery<GetAllAccessorials>(GET_ALL_ACCESSORIALS);
    const { data: regionsData } = useQuery<GetAllRegions>(GET_ALL_REGIONS);
    const { data: epCompaniesData } = useQuery<GetAllEpCompanies>(GET_ALL_EPCOMPANIES);
    const { data: customersData } = useQuery<GetAllCustomers>(GET_ALL_CUSTOMERS);
    const { data: statesData } = useQuery<GetAllStates>(GET_ALL_STATES);

    const accessorials: Option[] = [];
    const regions: Option[] = [];
    const customers: Suggestion[] = [];
    const epCompanies: Suggestion[] = [];
    const states: Suggestion[] = [];

    const [wellSiteLatitude, setWellSiteLatitude] = useState(0);
    const [wellSiteLongitude, setWellSiteLongitude] = useState(0);
    const [stagingGeofenceLatitude, setStagingGeofenceLatitude] = useState(0);
    const [stagingGeofenceLongitude, setStagingGeofenceLongitude] = useState(0);

    const isAddJobEnabled = useAuthContext().userHasPermission(PermissionEnum.EditJob);
    const isEditBellyDumpJobEnabled = useAuthContext().userHasPermission(PermissionEnum.EditBellyDumpJob);

    useEffect(() => {
        if (crewsData && crewsData.getAllCrews && !crewsLoading) {
            if (crewsData && crewsData.getAllCrews) {
                if (crewsData.getAllCrews.length === 0 && inputs.customerName !== '') {
                    setCustomerCrewError(customerCrewText);
                } else {
                    setCustomerCrewError('');
                    const customerCrews: Suggestion[] = [];
                    crewsData.getAllCrews.forEach((element: CrewType | null) => {
                        if (element?.crewNumber != null) {
                            customerCrews.push({
                                id: element.id,
                                name: element.crewNumber,
                            });
                        }
                    });
                    setCrews(customerCrews);
                }
            }
        }
    }, [crewsData, crewsLoading]);

    if (accessorialsData && accessorialsData.getAllAccessorials) {
        accessorialsData.getAllAccessorials.forEach((element: AccessorialType | null) => {
            if (element?.accessorialName != null) {
                accessorials.push({
                    id: element.accessorialId,
                    name: element.accessorialName,
                });
            }
        });
    }
    accessorials.push({ id: '0', name: 'None' });

    if (regionsData?.getAllRegions) {
        regionsData.getAllRegions.forEach((element: RegionType | null) => {
            if (element?.regionName != null) {
                regions.push({
                    id: element.id,
                    name: element.regionName,
                });
            }
        });
    }

    if (epCompaniesData?.getAllEpCompanies) {
        epCompaniesData.getAllEpCompanies.forEach((element: EPCompanyType | null) => {
            if (element?.companyName != null) {
                epCompanies.push({
                    id: element.id,
                    name: element.companyName,
                });
            }
        });
    }

    if (customersData?.getAllCustomers) {
        customersData.getAllCustomers.forEach((element: CustomerType | null) => {
            if (element?.customerName != null && element?.id != null) {
                customers.push({
                    id: element.id,
                    name: element.customerName,
                });
            }
        });
    }

    if (statesData?.getAllStates) {
        statesData.getAllStates.forEach((element: StateType | null) => {
            if (element?.stateName != null && element?.id != null) {
                states.push({
                    id: element.id,
                    name: element.stateName,
                });
            }
        });
    }

    const getRegionDistricts = (regionId: string): void => {
        const selectedRegion = regionsData?.getAllRegions?.filter((item: RegionType | null) => item?.id === +regionId)[0];
        const districtsList: Option[] = [];

        if (selectedRegion?.districts) {
            selectedRegion.districts.forEach((element: DistrictType | null) => {
                if (element?.districtName != null) {
                    districtsList.push({
                        id: element.id,
                        name: element.districtName,
                    });
                }
            });
            setDistrictName('');
        }

        setDistricts(districtsList);
    };

    const handleToggleChange=(checked) =>{
        setIsBellyDump(checked);
        inputs.isBellyDump = checked;
    };

    const onDistrictChange = (districtId: any): void => {
        if (districts.length > 0) {
            const selectedDistrict = districts.filter((item: Option) => item.id === +districtId)[0];
            setDistrictName(`${selectedDistrict.name}` || '');
        }
        setIsSaveDisabled(false);
    };

    const onRegionChange = (regionId: any): void => {
        getRegionDistricts(regionId);
        const regionCheck = districts.filter((item: Option) => item.id === +regionId)[0];
        if (!regionCheck) {
            setDistrictName('');
        }
        setIsSaveDisabled(false);
    };

    const updateCrews = (selectedCustomer: string | null): void => {
        if (selectedCustomer !== null) {
            getCrewsByCustomer({
                variables: {
                    customerId: +selectedCustomer,
                },
            });
        }
        setIsSaveDisabled(false);
    };

    const [wellSiteGeoFenceType, setWellSiteGeoFenceType] = useState(inputs.wellsiteGeoFenceType);
    const [stagingGeoFenceType, setstagingGeoFenceType] = useState(inputs.stagingGeoFenceType);

    const changeGeoFenceType = (eventkey: any): void => {
        if (eventkey == '') return;
        const numId = parseInt(eventkey);
        const filteredType = geoFenceTypes.filter((item: Option) => item.id === numId);
        const type = filteredType[0].name.toString();
        inputs.wellsiteGeoFenceType = type;
        setWellSiteGeoFenceType(type);
        if (type == 'Circle') {
            inputs.wellsiteGeoFenceRadius = 300;
        }
        else {
            inputs.wellsiteGeoFenceRadius = 0;
            fieldErrors.wellSiteRadiusError = '';
        }
    };

    const changeStagingGeoFenceType = (eventkey: any): void => {
        if (eventkey == '') return;
        const numId = parseInt(eventkey);
        const filteredType = geoFenceTypes.filter((item: Option) => item.id === numId);
        const type = filteredType[0].name.toString();
        inputs.stagingGeoFenceType = type;
        setstagingGeoFenceType(type);
        if (type == 'Circle') {
            inputs.stagingGeoFenceRadius = 300;
        }
        else {
            inputs.stagingGeoFenceRadius = 0;
            fieldErrors.stagingRadiusError = '';
        }
    };

    const clearStates = () => {
        setWellSiteLatitude(0);
        setWellSiteLongitude(0);
        setStagingGeofenceLatitude(0);
        setStagingGeofenceLongitude(0);
        setIsBellyDump(false);
    }

    const handleDissmis = (): void => {
        clearStates();
        setShow(false);
        setMutationError('');
        setIsSaveDisabled(false);
        resetFormState();
        setDistrictName('');
        setWellSiteGeoFenceType('Circle');
        setstagingGeoFenceType('');
        setIsBellyDump(false);
    };

    const handleMapDissmis = (): void => {
        setShowStagingMap(false);
        setShowWellSiteMap(false);
    };

    const addNewJob = (): void => {
        handleSubmit();
    };

    const handleOnWellSiteEdit = (polygonCoordinates: LatLng[]) => {
        inputs.wellsitePolygonCoordinates = polygonCoordinates;
        setIsSaveDisabled(false);
    };

    const handleOnStagingEdit = (polygonCoordinates: LatLng[]) => {
        inputs.stagingPolygonCoordinates = polygonCoordinates;
        setIsSaveDisabled(false);
    };

    const handleOnWellSiteCircleDrag = newCoords => {
        inputs.latitude = newCoords.lat();
        inputs.longitude = newCoords.lng();
        setIsSaveDisabled(false);
    };

    const handleOnWellSiteRadiusEdit = (newRadius: number) => {
        inputs.wellsiteGeoFenceRadius = Math.round(newRadius);
        setIsSaveDisabled(false);
    };

    const handleOnStagingCircleDrag = newCoords => {
        inputs.stagingLatitde = newCoords.lat();
        inputs.stagingLongitude = newCoords.lng();
        setIsSaveDisabled(false);
    };

    const handleOnStagingRadiusEdit = (newRadius: number) => {
        inputs.stagingGeoFenceRadius = Math.round(newRadius);
        setIsSaveDisabled(false);
    };

    const handleWellSiteLatLngChange = (lat: number, lng: number) => {
        inputs.latitude = lat;
        inputs.longitude = lng;
        setWellSiteLatitude(lat);
        setWellSiteLongitude(lng);
    }

    const handleStagingLatLngChange = (lat: number, lng:  number) => {
        inputs.stagingLatitude = lat;
        inputs.stagingLongitude = lng;
        setStagingGeofenceLatitude(lat);
        setStagingGeofenceLongitude(lng);
    }

    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;
    const searchLocationIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'crosshairs' }} />;

    return (
        <React.Fragment>
            {isAddJobEnabled && (
                <div className="Row AddJob-X-Button" onClick={() => setShow(true)}>
                    <i className="material-icons Header-Icon">add_circle</i>
                    <span className="TableListHeader-Link">Add a Job</span>
                </div>
            )}
            <Modal show={show} onHide={handleDissmis} size="xl">
                <Modal.Header className="AddJob-Header">
                    <div className="AddJob-Icon-Container">
                        <i className="material-icons AddJob-X-Button" onClick={handleDissmis}>
                            close
                        </i>
                    </div>
                    <div className="AddJob-Header-Container">
                        <div className="AddJob-Header-Title">Add a Job</div>
                        <span className="AddJob-Error">{mutationError}</span>
                        <div className="AddJob-Header-Buttons">
                            <CustomButton
                                additionalStyle={'AddJob-Header-Buttons-Additional'}
                                isBordered
                                title="Cancel"
                                onClick={handleDissmis}
                            ></CustomButton>
                            <CustomButton
                                title="Save"
                                isDisabled={isSaveDisabled}
                                onClick={() => {
                                    setMutationError('');
                                    setIsSaveDisabled(true);
                                    addNewJob();
                                }}
                            ></CustomButton>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="AddJob-Body">
                    <div className="AddJob-Form">
                        <div className="AddJob-Job-Profile-Title">Job Details</div>
                        <div className="AddJob-FormContainer">
                            <br></br>
                            {isEditBellyDumpJobEnabled && <Row>
                                <label>
                                    <Switch
                                        checked={isBellyDump}
                                        onChange={handleToggleChange}
                                        onColor="#b6bd00"
                                        onHandleColor="#f6f6f6"
                                        handleDiameter={24}
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        boxShadow="0px 1px 5px rgba(162, 193, 83, 0.6)"
                                        activeBoxShadow="0px 0px 1px 10px rgba(165, 205, 71, 0.2)"
                                        height={20}
                                        width={48}
                                        className="react-switch"
                                        id="material-switch"
                                    />
                                    <span className='AddJob-BTB-Toggle-Class'>Belly to box</span>
                                </label>
                            </Row>
                            }
                            <br></br>
                            <Row>
                                <Col md={6}>
                                    <SearchableDropdown
                                        autoCompleteEnabled={true}
                                        dropdownOptions={customers}
                                        errorMessage={fieldErrors.customerError || customerCrewError}
                                        hasError={fieldErrors.customerError !== '' || customerCrewError !== ''}
                                        id="customerName"
                                        label="CUSTOMER"
                                        onSelect={(eventKey: string | null) => {
                                            handleAutoCompleteChange('customerName', {
                                                id: eventKey,
                                                name: 'customerName',
                                            });
                                            updateCrews(eventKey);
                                        }}
                                        required
                                        endIcon={searchIcon}
                                    />
                                </Col>
                                <Col md={4}>
                                    <SearchableDropdown
                                        autoCompleteEnabled={true}
                                        dropdownOptions={epCompanies}
                                        errorMessage={fieldErrors.epCompanyError}
                                        hasError={fieldErrors.epCompanyError !== ''}
                                        id="epCompany"
                                        label="E&P COMPANY"
                                        onSelect={(eventKey: string | null) => {
                                            handleAutoCompleteChange('epCompany', {
                                                id: eventKey,
                                                name: 'epCompany',
                                            });
                                            setIsSaveDisabled(false);
                                        }}
                                        endIcon={searchIcon}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <Textfield
                                        label="JOB NAME"
                                        name="jobName"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            setIsSaveDisabled(false);
                                        }}
                                        error={fieldErrors.jobNameError !== ''}
                                        helperText={fieldErrors.jobNameError}
                                        required
                                        maxLength={40}
                                    />
                                </Col>
                                <Col md={4}>
                                    <div className="AddJob-AutoComplete-Container">
                                        <SearchableDropdown
                                            autoCompleteEnabled={true}
                                            dropdownOptions={crews}
                                            errorMessage={fieldErrors.crewError}
                                            hasError={fieldErrors.crewError !== ''}
                                            id="crew"
                                            label="CREW"
                                            onSelect={(eventKey: string | null) => {
                                                handleAutoCompleteChange('crew', {
                                                    id: eventKey,
                                                    name: 'crew',
                                                });
                                                setIsSaveDisabled(false);
                                            }}
                                            required
                                            endIcon={searchIcon}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <DateTimePicker
                                        dateOnly
                                        label="*PLANNED START DATE"
                                        value={inputs.startDate}
                                        onChange={(value: any) => {
                                            handleDateInputChange('startDate', value);
                                            setIsSaveDisabled(false);
                                        }}
                                        error={fieldErrors.startDateError || ''}
                                    />
                                </Col>
                                <Col md={4}>
                                    <DateTimePicker
                                        dateOnly
                                        label="*PLANNED END DATE"
                                        value={inputs.endDate}
                                        onChange={(value: any) => {
                                            handleDateInputChange('endDate', value);
                                            setIsSaveDisabled(false);
                                        }}
                                        error={fieldErrors.endDateError || ''}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4} className="AddJob-Region-Container">
                                    <SearchableDropdown
                                        dropdownOptions={regions}
                                        errorMessage={fieldErrors.regionError}
                                        hasError={fieldErrors.regionError !== ''}
                                        id="region"
                                        label="REGION"
                                        onSelect={(eventKey: string | null) => {
                                            handleAutoCompleteChange('region', {
                                                id: eventKey,
                                                name: 'region',
                                            });
                                            onRegionChange(eventKey);
                                        }}
                                        required
                                        endIcon={searchIcon}
                                    />
                                </Col>
                                <Col md={4} className="AddJob-Region-Container">
                                    <SearchableDropdown
                                        dropdownOptions={districts}
                                        errorMessage={fieldErrors.districtError}
                                        hasError={fieldErrors.districtError !== ''}
                                        id="district"
                                        label="DISTRICT"
                                        onSelect={(eventKey: string | null) => {
                                            handleAutoCompleteChange('district', {
                                                id: eventKey,
                                                name: 'district',
                                            });
                                            onDistrictChange(eventKey);
                                        }}
                                        value={districtName}
                                        required
                                        endIcon={searchIcon}
                                    />
                                </Col>
                            </Row>
                            <div className="AddJob-Address-Contact">Address and Contact Information</div>
                            <Row>
                                <Col md={4}>
                                    <Textfield
                                        label="CONTACT"
                                        name="contact"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                        }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Textfield
                                        label="PHONE NUMBER"
                                        name="phoneNumber"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            validatePhoneNumber(event.target.value);
                                        }}
                                        maxLength={10}
                                        error={fieldErrors.phoneNumberError !== ''}
                                        helperText={fieldErrors.phoneNumberError}
                                    />
                                </Col>
                                <Col md={5} />
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Textfield
                                        label="ADDRESS 1"
                                        name="addressLine1"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                        }}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Textfield
                                        label="ADDRESS 2"
                                        name="addressLine2"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                        }}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <Textfield
                                        label="CITY"
                                        name="city"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            setIsSaveDisabled(false);
                                        }}
                                        error={fieldErrors.cityError !== ''}
                                        helperText={fieldErrors.cityError}
                                        required
                                    />
                                </Col>
                                <Col md={3}>
                                    <div className="AddJob-AutoComplete-Container">
                                        <SearchableDropdown
                                            autoCompleteEnabled={true}
                                            dropdownOptions={states}
                                            errorMessage={fieldErrors.stateError}
                                            hasError={fieldErrors.stateError !== ''}
                                            id="stateName"
                                            label="STATE"
                                            onSelect={(eventKey: string | null) => {
                                                handleAutoCompleteChange('stateName', {
                                                    id: eventKey,
                                                    name: 'stateName',
                                                });
                                                setIsSaveDisabled(false);
                                            }}
                                            required
                                            endIcon={searchIcon}
                                        />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Textfield
                                        label="ZIP"
                                        name="zip"
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            validateZipNumber(event.target.value);
                                            setIsSaveDisabled(false);
                                        }}
                                        maxLength={5}
                                        error={fieldErrors.zipError !== ''}
                                        helperText={fieldErrors.zipError}
                                        required
                                    />
                                </Col>
                            </Row>
                            <div className="AddJob-Address-Contact" onClick={() => setShowStagingMap(true)}>
                                Wellsite Geofence
                            </div>
                            <Row>
                                <Col md={2}>
                                    <Textfield
                                        label="LATITUDE"
                                        name="latitude"
                                        value={wellSiteLatitude ? wellSiteLatitude : ''}
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            validateLatitude(event.target.value);
                                            setIsSaveDisabled(false);
                                        }}
                                        error={fieldErrors.latitudeError !== ''}
                                        helperText={fieldErrors.latitudeError || 'Well GPS Coordinates'}
                                        type="number"
                                        min="-90"
                                        max="90"
                                        step="0.1"
                                        endIcon={searchLocationIcon}
                                        required
                                    />
                                </Col>
                                <Col md={2}>
                                    <Textfield
                                        label="LONGITUDE"
                                        name="longitude"
                                        type="number"
                                        value={wellSiteLongitude ? wellSiteLongitude : ''}
                                        error={fieldErrors.longitudeError !== ''}
                                        helperText={fieldErrors.longitudeError}
                                        endIcon={searchLocationIcon}
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            validateLongitude(event.target.value);
                                            setIsSaveDisabled(false);
                                        }}
                                        required
                                    />
                                </Col>
                                <Col md={2} className="AddJob-Region-Container">
                                    <SearchableDropdown
                                        dropdownOptions={geoFenceTypes}
                                        errorMessage={fieldErrors.wellSiteGeoFenceTypeError}
                                        hasError={fieldErrors.wellSiteGeoFenceTypeError !== ''}
                                        id="GeofenceType"
                                        label="GEOFENCE TYPE"
                                        onSelect={(eventKey: string | null) => {
                                            // handleDropdownChange('wellsiteGeoFenceType', eventKey);
                                            changeGeoFenceType(eventKey);
                                        }}
                                        required
                                        value={wellSiteGeoFenceType}
                                    />
                                </Col>
                                <Col md={2}>
                                    {wellSiteGeoFenceType == 'Circle' && (
                                        <Textfield
                                            label="RADIUS (Meters)"
                                            name="wellsiteGeoFenceRadius"
                                            type="number"
                                            error={fieldErrors.wellSiteRadiusError !== ''}
                                            helperText={fieldErrors.wellSiteRadiusError}
                                            onChange={(event: any) => {
                                                handleInputChange(event);
                                                setIsSaveDisabled(false);
                                            }}
                                            value={defaultGeoFenceRadius}
                                            required={wellSiteGeoFenceType == 'Circle'}
                                        />
                                    )}
                                </Col>
                                <Col md={3} className="AddJob-ViewGeofence-Button">
                                    <span
                                        className={
                                            inputs.latitude.toString() === '' ||
                                                inputs.longitude.toString() === '' ||
                                                inputs.latitude === 0 ||
                                                inputs.longitude === 0
                                                ? 'disabled-viewGeofence'
                                                : 'Geofence-ViewLink'
                                        }
                                        onClick={() => setShowWellSiteMap(true)}
                                    >
                                        View Geofence
                                    </span>
                                </Col>
                            </Row>
                            <div className="AddJob-Address-Contact">Staging Area (Optional)</div>
                            <Row>
                                <Col md={2}>
                                    <Textfield
                                        label="LATITUDE"
                                        name="stagingLatitude"
                                        value={stagingGeofenceLatitude ? stagingGeofenceLongitude : ''}
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            validateStagingLatitude(event.target.value);
                                            setIsSaveDisabled(false);

                                            if (event.target.value === "" && inputs.stagingLongitude === "") {
                                                inputs.stagingGeoFenceType = '';
                                                setstagingGeoFenceType('');
                                                inputs.stagingGeoFenceRadius = 0;
                                            } else if (stagingGeoFenceType === '') {
                                                inputs.stagingGeoFenceType = 'Circle';
                                                setstagingGeoFenceType('Circle');
                                                inputs.stagingGeoFenceRadius = 300;
                                            }
                                        }}
                                        error={fieldErrors.stagingLatitudeError !== ''}
                                        helperText={fieldErrors.stagingLatitudeError || 'Stage GPS Coordinates'}
                                        type="number"
                                        min="-90"
                                        max="90"
                                        step="0.1"
                                        endIcon={searchLocationIcon}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Textfield
                                        label="LONGITUDE"
                                        name="stagingLongitude"
                                        type="number"
                                        value={stagingGeofenceLongitude ? stagingGeofenceLongitude : ''}
                                        error={fieldErrors.stagingLongitudeError !== ''}
                                        helperText={fieldErrors.stagingLongitudeError}
                                        endIcon={searchLocationIcon}
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                            validateStagingLongitude(event.target.value);
                                            setIsSaveDisabled(false);

                                            if (event.target.value === "" && inputs.stagingLatitude === "") {
                                                inputs.stagingGeoFenceType = '';
                                                setstagingGeoFenceType('');
                                                inputs.stagingGeoFenceRadius = 0;
                                            } else if (stagingGeoFenceType === '') {
                                                inputs.stagingGeoFenceType = 'Circle';
                                                setstagingGeoFenceType('Circle');
                                                inputs.stagingGeoFenceRadius = 300;
                                            }
                                        }}
                                    />
                                </Col>
                                <Col md={2} className="AddJob-Region-Container">
                                    <SearchableDropdown
                                        dropdownOptions={geoFenceTypes}
                                        errorMessage={fieldErrors.stagingGeofenceTypeError}
                                        hasError={fieldErrors.stagingGeofenceTypeError !== ''}
                                        id="stagingGeoFenceType"
                                        label="GEOFENCE TYPE"
                                        onSelect={(eventKey: string | null) => {
                                            // handleDropdownChange('stagingGeoFenceType', eventKey);
                                            changeStagingGeoFenceType(eventKey);
                                        }}
                                        value={stagingGeoFenceType}
                                        required={inputs.stagingLatitude !== '' || inputs.stagingLongitude !== ''}
                                    />
                                </Col>
                                <Col md={2}>
                                    {stagingGeoFenceType == 'Circle' && (
                                        <Textfield
                                            label="RADIUS (Meters)"
                                            name="stagingGeoFenceRadius"
                                            type="number"
                                            error={fieldErrors.stagingRadiusError !== ''}
                                            helperText={fieldErrors.stagingRadiusError}
                                            onChange={(event: any) => {
                                                handleInputChange(event);
                                                setIsSaveDisabled(false);
                                            }}
                                            value={defaultGeoFenceRadius}
                                            required
                                        />
                                    )}
                                </Col>
                                <Col md={3} className="AddJob-ViewGeofence-Button">
                                    <span
                                        className={
                                            inputs.stagingLatitude.toString() === '' ||
                                                inputs.stagingLongitude.toString() === '' ||
                                                inputs.stagingLatitude === 0 ||
                                                inputs.stagingLongitude === 0
                                                ? 'disabled-viewGeofence'
                                                : 'Geofence-ViewLink'
                                        }
                                        onClick={() => setShowStagingMap(true)}
                                    >
                                        View Geofence
                                    </span>
                                </Col>
                            </Row>
                            <div className="AddJob-Notes-Container">
                                <div className="AddJob-Notes">DIRECTIONS</div>
                                <textarea
                                    className="AddJob-Directions-Paragraph"
                                    name="directions"
                                    rows={2}
                                    onChange={event => {
                                        handleInputChange(event);
                                    }}
                                ></textarea>
                            </div>
                            <div className="AddJob-Address-Contact">Accessorial</div>
                            <Row>
                                <Col md={5} className="AddJob-Region-Container">
                                    <SearchableDropdown
                                        dropdownOptions={accessorials}
                                        id="accessorial"
                                        label="SELECT ACCESSORIAL TO INCLUDE"
                                        onSelect={(eventKey: string | null) => {
                                            handleDropdownChange('accessorial', eventKey);
                                        }}
                                    />
                                </Col>
                                {inputs.accessorial !== 0 && (
                                    <Col md={3}>
                                        <Textfield
                                            label="MILES"
                                            name="miles"
                                            onChange={(event: any) => {
                                                handleInputChange(event);
                                            }}
                                            error={fieldErrors.milesError !== ''}
                                            helperText={fieldErrors.milesError}
                                            required
                                        />
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <div>
                <Modal show={showWellSiteMap} onHide={handleMapDissmis} size="xl">
                    <Modal.Header className="AddJob-Header">
                        <div className="AddJob-Icon-Container">
                            <div className="AddJob-Header-Buttons">
                                <CustomButton
                                    additionalStyle={'AddJob-Header-Buttons-Additional'}
                                    isBordered
                                    title="Cancel"
                                    onClick={handleMapDissmis}
                                ></CustomButton>
                                <CustomButton
                                    title="Save"
                                    onClick={() => {
                                        handleMapDissmis();
                                    }}
                                ></CustomButton>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        <GeofenceMap
                            latitude={parseFloat(inputs.latitude)}
                            longitude={parseFloat(inputs.longitude)}
                            selectedPolygonCoordinates={
                                inputs.wellsitePolygonCoordinates.length > 2
                                    ? inputs.wellsitePolygonCoordinates
                                    : { lat: parseFloat(inputs.latitude), lng: parseFloat(inputs.longitude) }
                            }
                            radius={inputs.wellsiteGeoFenceRadius}
                            geofenceType={inputs.wellsiteGeoFenceType}
                            onEditMap={handleOnWellSiteEdit}
                            onCircleDrag={handleOnWellSiteCircleDrag}
                            onEditCircleRadius={handleOnWellSiteRadiusEdit}
                            onChangeWellSiteLatLng={handleWellSiteLatLngChange}
                        />
                    </Modal.Body>
                </Modal>
                <Modal show={showStagingMap} onHide={handleMapDissmis} size="xl">
                    <Modal.Header className="AddJob-Header">
                        <div className="AddJob-Icon-Container">
                            <div className="AddJob-Header-Buttons">
                                <CustomButton
                                    additionalStyle={'AddJob-Header-Buttons-Additional'}
                                    isBordered
                                    title="Cancel"
                                    onClick={handleMapDissmis}
                                ></CustomButton>
                                <CustomButton
                                    title="Save"
                                    onClick={() => {
                                        handleMapDissmis();
                                    }}
                                ></CustomButton>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        <GeofenceMap
                            latitude={parseFloat(inputs.stagingLatitude)}
                            longitude={parseFloat(inputs.stagingLongitude)}
                            selectedPolygonCoordinates={
                                inputs.stagingPolygonCoordinates.length > 2
                                    ? inputs.stagingPolygonCoordinates
                                    : {
                                        lat: parseFloat(inputs.stagingLatitude),
                                        lng: parseFloat(inputs.stagingLongitude),
                                    }
                            }
                            radius={inputs.stagingGeoFenceRadius}
                            geofenceType={inputs.stagingGeoFenceType}
                            onEditMap={handleOnStagingEdit}
                            onCircleDrag={handleOnStagingCircleDrag}
                            onEditCircleRadius={handleOnStagingRadiusEdit}
                            onChangeStagingLatLng={handleStagingLatLngChange}
                        />
                    </Modal.Body>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default InitialModal;

